import { FormattedMessage } from 'react-intl';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import { DateHistoryEntry, ObservationKey } from '../types';
import { TableBody, TableHead, TableRoot } from '@/shared/components/Table';
import DateCell from './DateCell';

interface Props {
  entries: DateHistoryEntry[];
}

const columnHelper = createColumnHelper<DateHistoryEntry>();

const timeWindows: { id: ObservationKey; messageId: string; comparedTo: ObservationKey | null }[] = [
  { id: 'last_7d', messageId: 'stats.table.d7', comparedTo: null },
  { id: 'last_24h', messageId: 'stats.table.h24', comparedTo: 'last_7d' },
  { id: 'last_12h', messageId: 'stats.table.h12', comparedTo: 'last_24h' },
  { id: 'last_6h', messageId: 'stats.table.h6', comparedTo: 'last_12h' },
];

const columns = [
  columnHelper.accessor('facility_name', {
    header: () => <FormattedMessage id="stats.table.facility" />,
    cell: (info) => (
      <span className="font-medium text-slate-900">
        {info.getValue()}
      </span>
    ),
  }),
  ...timeWindows.map(({ id, messageId, comparedTo }) =>
    columnHelper.display({
      id,
      header: () => <FormattedMessage id={messageId} />,
      cell: ({ row }) => (
        <DateCell
          observation={row.original[id]}
          comparisonObservation={comparedTo ? row.original[comparedTo] : undefined}
        />
      ),
    })
  ),
];

export default function DateHistoryTable({ entries }: Props) {
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table is not compatible with React Compiler memoization
  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (entries.length === 0) {
    return null;
  }

  return (
    <TableRoot table={table} tableClassName="min-w-[720px]">
      <TableHead />
      <TableBody />
    </TableRoot>
  );
}
